// @tennis-matches/services/tournament — Registrierungsphase (vor Spielplan)

import {
  isTournamentStartInPast,
  parseTournamentStartLocal,
} from "./tournamentStartDateTime.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

/** @typedef {'link'|'club'|'platform'} MatchmixerRegistrationScope */
/** @typedef {'manual'|'link'|'hybrid'} TournamentPlayerEntryMode */

export const MATCHMIXER_REGISTRATION_SCOPES = ["link", "club", "platform"];
export const TOURNAMENT_PLAYER_ENTRY_MODES = ["manual", "link", "hybrid"];

const SCOPE_SET = new Set(MATCHMIXER_REGISTRATION_SCOPES);
const ENTRY_MODE_SET = new Set(TOURNAMENT_PLAYER_ENTRY_MODES);

/**
 * @returns {{ open: boolean, scope: MatchmixerRegistrationScope, closesAt: string, openedAt: string, entryMode: TournamentPlayerEntryMode }}
 */
export function defaultTournamentRegistration() {
  return {
    open: false,
    scope: "link",
    closesAt: "",
    openedAt: "",
    entryMode: "manual",
  };
}

export function normalizeTournamentRegistration(raw) {
  const base = defaultTournamentRegistration();
  if (!raw || typeof raw !== "object") return base;
  const scope = __asStr(raw.scope).toLowerCase();
  const entryMode = __asStr(raw.entryMode).toLowerCase();
  return {
    open: raw.open === true,
    scope: SCOPE_SET.has(scope) ? scope : "club",
    closesAt: __asStr(raw.closesAt),
    openedAt: __asStr(raw.openedAt),
    entryMode: ENTRY_MODE_SET.has(entryMode) ? entryMode : "manual",
  };
}

export function registrationToPersistPayload(registration) {
  return normalizeTournamentRegistration(registration);
}

function __withOrigin(path, origin = "") {
  const p = __asStr(path);
  if (!p) return "";
  const base =
    __asStr(origin) ||
    (typeof globalThis !== "undefined" && globalThis.location?.origin
      ? String(globalThis.location.origin)
      : "");
  if (!base) return p;
  return `${base.replace(/\/+$/, "")}${p}`;
}

/**
 * Öffentlicher Anmelde-Pfad (ohne Query `code=`, das kollidiert mit OAuth-PKCE).
 * @param {string} code
 */
export function buildMatchmixerRegistrationPath(code) {
  const c = __asStr(code);
  if (!c) return "";
  return `/matchmixer/anmelden/${encodeURIComponent(c)}`;
}

/**
 * Persönlicher Teilnehmer-Pfad (Ergebnisse, Status).
 * @param {string} editToken
 */
export function buildMatchmixerPlayerCarePath(editToken) {
  const token = __asStr(editToken);
  if (!token) return "";
  return `/matchmixer/me/${encodeURIComponent(token)}`;
}

/**
 * @param {string} code
 * @param {string} [origin]
 */
export function buildMatchmixerRegistrationPublicUrl(code, origin = "") {
  return __withOrigin(buildMatchmixerRegistrationPath(code), origin);
}

/**
 * @param {string} editToken
 * @param {string} [origin]
 */
export function buildMatchmixerPlayerCareUrl(editToken, origin = "") {
  return __withOrigin(buildMatchmixerPlayerCarePath(editToken), origin);
}

/**
 * Setup-Übersicht (kein Wizard, kein ?new=1).
 * @param {string} code
 */
export function buildMatchmixerSetupPath(code) {
  const c = __asStr(code);
  if (!c) return "/matchmixer/setup";
  return `/matchmixer/setup?code=${encodeURIComponent(c)}`;
}

/** Snapshot/Meta nachladen, auch wenn Persist denselben Code schon kennt. */
export function shouldFetchMatchmixerSetupSnapshot({
  urlCode,
  wantNew = false,
  alreadyAppliedCode = "",
} = {}) {
  const code = __asStr(urlCode);
  if (!code || wantNew) return false;
  return __asStr(alreadyAppliedCode) !== code;
}

/** Leeres Bootstrap nur bei echtem not-found — nicht bei Auth-/DB-Fehlern. */
export function shouldCreateEmptyMatchmixerBootstrap(metaLoadReason) {
  return __asStr(metaLoadReason) === "not-found";
}

/**
 * Veranstalter → Setup; sonst öffentliche Anmeldung.
 * @param {{ code?: string, organizerUserId?: string, organizer_user_id?: string }} item
 * @param {string} [currentUserId]
 */
export function resolveMatchmixerHubOpenPath(item, currentUserId = "") {
  const c = __asStr(item?.code);
  if (!c) return "/hub/matchmixer";
  const org = __asStr(item?.organizerUserId || item?.organizer_user_id);
  const uid = __asStr(currentUserId);
  if (org && uid && org === uid) return buildMatchmixerSetupPath(c);
  return buildMatchmixerRegistrationPath(c);
}

/**
 * Turnier-Code aus `/matchmixer/anmelden/:code` oder `?code=`.
 * @param {{ get?: (k: string) => string | null }} [searchParams]
 * @param {string} [pathname]
 */
export function readMatchmixerJoinCode(searchParams, pathname = "") {
  const fromQuery = __asStr(
    searchParams && typeof searchParams.get === "function"
      ? searchParams.get("code")
      : "",
  );
  if (fromQuery) return fromQuery;
  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean);
  const i = parts.findIndex((p) => p === "anmelden");
  if (i < 0) return "";
  try {
    return decodeURIComponent(parts[i + 1] || "").trim();
  } catch {
    return __asStr(parts[i + 1]);
  }
}

/**
 * edit_token aus `/matchmixer/me/:token`.
 * @param {string} [pathname]
 */
export function readMatchmixerCareToken(pathname = "") {
  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean);
  const i = parts.findIndex((p) => p === "me");
  if (i < 0) return "";
  try {
    return decodeURIComponent(parts[i + 1] || "").trim();
  } catch {
    return __asStr(parts[i + 1]);
  }
}

/**
 * @param {Record<string, string>} existingNames
 * @param {Array<{ displayName?: string }>} registrations
 */
export function mergeRegistrationNamesIntoPlayers(
  existingNames,
  registrations,
) {
  const names =
    existingNames && typeof existingNames === "object"
      ? { ...existingNames }
      : {};
  const list = Array.isArray(registrations) ? registrations : [];
  const used = new Set(
    Object.values(names)
      .map((n) => __asStr(n).toLowerCase())
      .filter(Boolean),
  );
  const keys = Object.keys(names)
    .map((k) => Number(k))
    .filter((n) => Number.isFinite(n) && n > 0);
  let next = Math.max(0, ...keys) + 1;
  if (!Number.isFinite(next) || next < 1) next = 1;
  for (const row of list) {
    const name = __asStr(row?.displayName || row?.display_name);
    if (!name) continue;
    const key = name.toLowerCase();
    if (used.has(key)) continue;
    used.add(key);
    names[String(next)] = name;
    next += 1;
  }
  return names;
}

/**
 * @param {(key: string, params?: object) => string} t
 * @param {{ scope: string, closesAt: string, open: boolean }} registration
 */
export function formatRegistrationScopeSummary(t, registration) {
  const r = normalizeTournamentRegistration(registration);
  const scopeLabel =
    typeof t === "function"
      ? t(`matchmixer.registration.scope.${r.scope}`)
      : r.scope;
  if (!r.closesAt) return scopeLabel;
  let dateLabel = r.closesAt;
  try {
    const d = new Date(r.closesAt);
    if (!Number.isNaN(d.getTime())) {
      dateLabel = new Intl.DateTimeFormat("de-DE", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(d);
    }
  } catch (_) {
    // keep raw
  }
  return typeof t === "function"
    ? t("matchmixer.registration.scopeWithDeadline", {
        scope: scopeLabel,
        deadline: dateLabel,
      })
    : `${scopeLabel} · ${dateLabel}`;
}

/**
 * Anmeldefrist darf nicht nach Turnierbeginn liegen.
 * @returns {Date|null} Start lokal, sonst null
 */
export function registrationClosesAtMaxDate(startDate, startTime) {
  return parseTournamentStartLocal(startDate, startTime);
}

/**
 * ISO-String für datetime-local max / Default: Turnierbeginn, sonst leer.
 */
export function registrationClosesAtMaxLocalValue(startDate, startTime) {
  const start = registrationClosesAtMaxDate(startDate, startTime);
  if (!start) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}T${pad(start.getHours())}:${pad(start.getMinutes())}`;
}

/**
 * Vorschlag: min(jetzt+24h, Turnierbeginn). Ohne Start → jetzt+24h.
 */
export function suggestRegistrationClosesAt(
  startDate,
  startTime,
  now = new Date(),
) {
  const plus24 = new Date(now);
  plus24.setHours(plus24.getHours() + 24);
  plus24.setSeconds(0, 0);
  const start = registrationClosesAtMaxDate(startDate, startTime);
  if (!start) return plus24.toISOString();
  const capped = plus24.getTime() <= start.getTime() ? plus24 : start;
  return capped.toISOString();
}

/** Kürzt closesAt auf Turnierbeginn, falls danach. */
export function clampRegistrationClosesAt(closesAt, startDate, startTime) {
  const raw = __asStr(closesAt);
  if (!raw) return "";
  const closes = new Date(raw);
  if (Number.isNaN(closes.getTime())) return "";
  const start = registrationClosesAtMaxDate(startDate, startTime);
  if (!start) return closes.toISOString();
  if (closes.getTime() > start.getTime()) return start.toISOString();
  return closes.toISOString();
}

export function isRegistrationClosesAtAfterStart(
  closesAt,
  startDate,
  startTime,
) {
  const raw = __asStr(closesAt);
  if (!raw) return false;
  const closes = new Date(raw);
  if (Number.isNaN(closes.getTime())) return false;
  const start = registrationClosesAtMaxDate(startDate, startTime);
  if (!start) return false;
  return closes.getTime() > start.getTime();
}

export function isRegistrationPublishValid({
  registration,
  tournamentName,
  startDate,
  startTime,
}) {
  const r = normalizeTournamentRegistration(registration);
  if (!__asStr(tournamentName)) return false;
  if (!__asStr(startDate)) return false;
  if (isTournamentStartInPast(startDate, startTime)) return false;
  if (!r.closesAt) return false;
  const closes = new Date(r.closesAt);
  if (Number.isNaN(closes.getTime())) return false;
  if (closes.getTime() <= Date.now()) return false;
  if (isRegistrationClosesAtAfterStart(r.closesAt, startDate, startTime))
    return false;
  return true;
}
